var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
var loadGoogleMapsScript = function (apiKey) {
    return new Promise(function (resolve, reject) {
        var _a, _b, _c;
        if ((_c = (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.google) === null || _b === void 0 ? void 0 : _b.maps) === null || _c === void 0 ? void 0 : _c.places) {
            resolve();
            return;
        }
        var existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
        if (existingScript) {
            globalThis.initGooglePlacesAutocomplete = resolve;
            return;
        }
        var script = document.createElement('script');
        script.src = "https://maps.googleapis.com/maps/api/js?key=".concat(apiKey, "&libraries=places");
        script.async = true;
        script.defer = true;
        script.onload = function () { return resolve(); };
        script.onerror = function () { return reject(new Error('Failed to load Google Maps script')); };
        document.head.appendChild(script);
    });
};
function getAddressComponent(place, type) {
    var _a, _b;
    var component = (_a = place.address_components) === null || _a === void 0 ? void 0 : _a.find(function (c) { return c.types.includes(type); });
    return (_b = component === null || component === void 0 ? void 0 : component.long_name) !== null && _b !== void 0 ? _b : null;
}
function getCountryNames(place) {
    var _a, _b, _c;
    var component = (_a = place.address_components) === null || _a === void 0 ? void 0 : _a.find(function (c) { return c.types.includes('country'); });
    return {
        longName: (_b = component === null || component === void 0 ? void 0 : component.long_name) !== null && _b !== void 0 ? _b : null,
        shortName: (_c = component === null || component === void 0 ? void 0 : component.short_name) !== null && _c !== void 0 ? _c : null,
    };
}
function getAdminAreaLevel1(place) {
    var _a, _b, _c;
    var component = (_a = place.address_components) === null || _a === void 0 ? void 0 : _a.find(function (c) { return c.types.includes('administrative_area_level_1'); });
    return {
        longName: (_b = component === null || component === void 0 ? void 0 : component.long_name) !== null && _b !== void 0 ? _b : null,
        shortName: (_c = component === null || component === void 0 ? void 0 : component.short_name) !== null && _c !== void 0 ? _c : null,
    };
}
export var AddressPlacesAutocomplete = function (_a) {
    var _b, _c, _d;
    var apiKey = _a.apiKey, _e = _a.value, value = _e === void 0 ? '' : _e, onChange = _a.onChange, onLoadError = _a.onLoadError, _f = _a.editMode, editMode = _f === void 0 ? false : _f, textFieldProps = __rest(_a, ["apiKey", "value", "onChange", "onLoadError", "editMode"]);
    var inputRef = useRef(null);
    var autocompleteRef = useRef(null);
    var _g = useState(value), inputValue = _g[0], setInputValue = _g[1];
    var _h = useState(false), scriptLoaded = _h[0], setScriptLoaded = _h[1];
    var _j = useState(false), searching = _j[0], setSearching = _j[1];
    useEffect(function () {
        setInputValue(value);
    }, [value]);
    useEffect(function () {
        if (!apiKey)
            return;
        loadGoogleMapsScript(apiKey)
            .then(function () { return setScriptLoaded(true); })
            .catch(function (err) { return onLoadError === null || onLoadError === void 0 ? void 0 : onLoadError(err); });
    }, [apiKey, onLoadError]);
    var initAutocomplete = useCallback(function () {
        var _a, _b, _c;
        if (!scriptLoaded || !inputRef.current || !((_c = (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.google) === null || _b === void 0 ? void 0 : _b.maps) === null || _c === void 0 ? void 0 : _c.places))
            return;
        if (autocompleteRef.current)
            return;
        var autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
            types: ['address'],
            fields: ['address_components', 'formatted_address', 'geometry'],
        });
        autocomplete.addListener('place_changed', function () {
            var _a, _b, _c, _d, _e, _f;
            var place = autocomplete.getPlace();
            if (!place.formatted_address)
                return;
            var address = place.formatted_address;
            var locality = getAddressComponent(place, 'locality');
            var _g = getAdminAreaLevel1(place), adminLong = _g.longName, adminShort = _g.shortName;
            var administrativeAreaLevel2 = getAddressComponent(place, 'administrative_area_level_2');
            var _h = getCountryNames(place), countryLong = _h.longName, countryShort = _h.shortName;
            var postalCode = getAddressComponent(place, 'postal_code');
            var city = locality !== null && locality !== void 0 ? locality : administrativeAreaLevel2;
            var stateProvince = adminLong;
            // Use null when Google returns no distinct ISO code (short_name === long_name means no subdivision code exists)
            var stateProvinceCode = adminShort && adminShort !== adminLong ? adminShort : null;
            var region = adminLong !== null && adminLong !== void 0 ? adminLong : administrativeAreaLevel2;
            var lat = (_c = (_b = (_a = place.geometry) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.lat) === null || _c === void 0 ? void 0 : _c.call(_b);
            var lng = (_f = (_e = (_d = place.geometry) === null || _d === void 0 ? void 0 : _d.location) === null || _e === void 0 ? void 0 : _e.lng) === null || _f === void 0 ? void 0 : _f.call(_e);
            var components = {
                address: address,
                city: city,
                region: region,
                stateProvince: stateProvince,
                stateProvinceCode: stateProvinceCode,
                countryName: countryLong !== null && countryLong !== void 0 ? countryLong : '',
                countryCode: countryShort !== null && countryShort !== void 0 ? countryShort : null,
                postalCode: postalCode,
                latitude: typeof lat === 'number' ? lat : null,
                longitude: typeof lng === 'number' ? lng : null,
            };
            setInputValue(address);
            onChange === null || onChange === void 0 ? void 0 : onChange(address, components);
            if (editMode) {
                setSearching(false);
            }
        });
        autocompleteRef.current = autocomplete;
    }, [scriptLoaded, onChange, editMode]);
    // Always initialize autocomplete once script is ready.
    // In editMode the input is hidden until searching, but Google attaches fine to hidden inputs.
    useEffect(function () {
        initAutocomplete();
    }, [initAutocomplete]);
    useEffect(function () {
        return function () {
            var _a, _b, _c, _d;
            if (autocompleteRef.current) {
                (_d = (_c = (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.google) === null || _b === void 0 ? void 0 : _b.maps) === null || _c === void 0 ? void 0 : _c.event) === null || _d === void 0 ? void 0 : _d.clearInstanceListeners(autocompleteRef.current);
                autocompleteRef.current = null;
            }
        };
    }, []);
    // Inject z-index fix for pac-container so it appears above MUI Dialog (z-index 1300)
    useEffect(function () {
        if (!editMode)
            return;
        var style = document.createElement('style');
        style.textContent = '.pac-container { z-index: 1400 !important; }';
        document.head.appendChild(style);
        return function () {
            style.remove();
        };
    }, [editMode]);
    var handleInputChange = function (e) {
        var newValue = e.target.value;
        setInputValue(newValue);
        if (!onChange)
            return;
        if (!newValue) {
            onChange('', {
                address: '',
                city: null,
                region: null,
                stateProvince: null,
                stateProvinceCode: null,
                countryName: '',
                countryCode: null,
                postalCode: null,
                latitude: null,
                longitude: null,
            });
        }
    };
    var handleInputBlur = function () {
        if (!onChange || !inputValue || inputValue === value)
            return;
        onChange(inputValue, {
            address: inputValue,
            city: null,
            region: null,
            stateProvince: null,
            stateProvinceCode: null,
            countryName: '',
            countryCode: null,
            postalCode: null,
            latitude: null,
            longitude: null,
        });
    };
    var handleSearchClick = function () {
        // flushSync ensures the autocomplete input is visible in the DOM before we focus it
        flushSync(function () { return setSearching(true); });
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
    };
    var handleCancelSearch = function () {
        setSearching(false);
    };
    if (editMode) {
        return (_jsxs(_Fragment, { children: [!searching && (_jsx(TextField, __assign({}, textFieldProps, { value: inputValue, onChange: function (e) {
                        setInputValue(e.target.value);
                        onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value, {
                            address: e.target.value,
                            city: null,
                            region: null,
                            stateProvince: null,
                            stateProvinceCode: null,
                            countryName: '',
                            countryCode: null,
                            postalCode: null,
                            latitude: null,
                            longitude: null,
                        });
                    }, placeholder: (_b = textFieldProps.placeholder) !== null && _b !== void 0 ? _b : 'Address', InputProps: __assign({ endAdornment: (_jsx(InputAdornment, __assign({ position: "end" }, { children: _jsx(IconButton, __assign({ size: "small", onClick: handleSearchClick, "aria-label": "search address" }, { children: _jsx(FontAwesomeIcon, { icon: "magnifying-glass" }) })) }))) }, textFieldProps.InputProps) }))), _jsx(TextField, __assign({}, textFieldProps, { inputRef: inputRef, sx: __assign(__assign({}, textFieldProps.sx), { display: searching ? undefined : 'none' }), placeholder: (_c = textFieldProps.placeholder) !== null && _c !== void 0 ? _c : 'Search to replace address…', InputProps: __assign({ startAdornment: (_jsx(InputAdornment, __assign({ position: "start" }, { children: _jsx(FontAwesomeIcon, { icon: "magnifying-glass" }) }))), endAdornment: (_jsx(InputAdornment, __assign({ position: "end" }, { children: _jsx(IconButton, __assign({ size: "small", onClick: handleCancelSearch, "aria-label": "cancel search" }, { children: _jsx(FontAwesomeIcon, { icon: "xmark" }) })) }))) }, textFieldProps.InputProps) }))] }));
    }
    return (_jsx(TextField, __assign({}, textFieldProps, { inputRef: inputRef, value: inputValue, onChange: handleInputChange, onBlur: handleInputBlur, placeholder: (_d = textFieldProps.placeholder) !== null && _d !== void 0 ? _d : 'Enter address', InputProps: __assign({ startAdornment: (_jsx(InputAdornment, __assign({ position: "start" }, { children: _jsx(FontAwesomeIcon, { icon: "magnifying-glass" }) }))) }, textFieldProps.InputProps) })));
};
